import { createContext, ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from './Modal';

export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'danger' for deletes and other destructive actions. */
  tone?: 'default' | 'danger';
}

type ConfirmFn = (opts: ConfirmOptions | string) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * App-wide replacement for window.confirm(): `const confirm = useConfirm();
 * if (!(await confirm({ title, message, tone: 'danger' }))) return;`
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((o) => {
    resolver.current?.(false);
    setOpts(typeof o === 'string' ? { title: o } : o);
    return new Promise<boolean>((resolve) => { resolver.current = resolve; });
  }, []);

  const settle = (ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setOpts(null);
  };

  const danger = opts?.tone === 'danger';

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {opts && (
        <Modal
          size="sm"
          title={
            <span className="flex items-center gap-2">
              {danger && <AlertTriangle className="h-4 w-4 text-red-600" />}
              {opts.title}
            </span>
          }
          onClose={() => settle(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => settle(false)}>
                {opts.cancelLabel ?? 'Cancel'}
              </button>
              <button
                type="button"
                autoFocus
                className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
                onClick={() => settle(true)}
              >
                {opts.confirmLabel ?? (danger ? 'Delete' : 'Confirm')}
              </button>
            </>
          }
        >
          {opts.message && <div className="text-sm text-slate-600">{opts.message}</div>}
        </Modal>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext);
  if (!fn) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return fn;
}
